import { useEffect, useState } from "react";
import { Input } from "@mui/joy";
import { useNavigate, useSearchParams } from "react-router-dom";
import CustomPagination from "../../components/CustomPagination";
import { useDebounce } from "../Products/utils";
import { admin_bids_list } from "../../reduxData/user/userAction";
import { Countdown, EmptyRow, FilterTabs, SkeletonRows, StatusBadge, Thumb, formatDate, money, pick, reveal } from "./shared";

const FILTERS = [
  { label: "All", value: "" },
  { label: "Live", value: "live" },
  { label: "Won", value: "won" },
  { label: "Closed", value: "closed" },
];

const BuyerBids = () => {
  const [records, setRecords] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [filter, setFilter] = useState("");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const debouncedSearch = useDebounce(search, 300);
  const [searchParams] = useSearchParams();
  const auctionId = searchParams.get("auction_id");
  const navigate = useNavigate();

  useEffect(() => {
    setPage(1);
  }, [filter, debouncedSearch]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const params = { page, limit };
        if (filter) params.filter = filter;
        if (debouncedSearch) params.search = debouncedSearch;
        if (auctionId) params.auction_id = auctionId;
        const res = await admin_bids_list(params);
        const body = res?.data;
        setRecords(body?.data?.bids || body?.data || []);
        setTotal(
          body?.pagination?.total_records ?? body?.total ?? body?.pagination?.total ?? body?.data?.total ?? 0
        );
        window.scrollTo(0, 0);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [page, limit, filter, debouncedSearch, auctionId]);

  return (
    <div className="container la-page">
      <div className="la-page-head">
        <div>
          <h3>Buyer Bids</h3>
          <p>Every bid placed by buyers across live auctions.</p>
        </div>
        {!loading && <span className="la-count">{total} bids</span>}
      </div>
      <div className="la-toolbar">
        <div className="d-flex align-items-center gap-2 flex-wrap">
          <Input
            type="text"
            placeholder="Search bids..."
            startDecorator={<i className="bi bi-search"></i>}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ minWidth: 280 }}
          />
          {auctionId && (
            <button className="la-filter-pill" onClick={() => navigate("/buyer-bids")}>
              Auction #{auctionId} <i className="bi bi-x-lg ms-1"></i>
            </button>
          )}
        </div>
        <FilterTabs options={FILTERS} value={filter} onChange={setFilter} />
      </div>
      <div className="la-card">
        <div className="la-table-wrap">
          <table className="la-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Buyer</th>
                <th>Product</th>
                <th>Bid Status</th>
                <th className="num">Bid Amount</th>
                <th className="num">Current Price</th>
                <th className="num">Shipping Fee</th>
                <th>Time Left</th>
                <th>Placed At</th>
                <th></th>
              </tr>
            </thead>
            <tbody key={`${page}-${filter}-${debouncedSearch}`}>
              {loading ? (
                <SkeletonRows cols={10} />
              ) : records.length > 0 ? (
                records.map((i, idx) => {
                  const id = i?._id || i?.id;
                  const status = pick(i, "bid_status", "status");
                  const isOpen = ["live", "active"].includes(String(i?.auction_status || status).toLowerCase());
                  return (
                    <tr key={id || idx} className="la-row-link" style={reveal(idx)} onClick={() => navigate(`/buyer-bids/${id}`)}>
                      <td>{(page - 1) * limit + idx + 1}</td>
                      <td>
                        <div className="la-title">{pick(i, "bidder_name", "buyer_name", "buyer.name", "bidder_email") || "-"}</div>
                        <div className="la-sub">{(pick(i, "bidder_name", "buyer_name", "buyer.name") && pick(i, "bidder_email", "buyer_email", "buyer.email")) || ""}</div>
                      </td>
                      <td>
                        <div className="la-product">
                          <Thumb src={pick(i, "images.0", "image", "product.images.0")} />
                          <div className="la-title">
                            {pick(i, "product_name", "title", "model", "product.product_name") || "-"}
                          </div>
                        </div>
                      </td>
                      <td><StatusBadge status={status} /></td>
                      <td className="num la-amount" style={{ color: "var(--la-brand)" }}>{money(pick(i, "bid_amount", "amount"))}</td>
                      <td className="num">{money(i?.current_price)}</td>
                      <td className="num">{money(i?.shipping_fee)}</td>
                      <td>{isOpen && i?.end_at ? <span className="la-time"><i className="bi bi-clock"></i><Countdown end={i.end_at} /></span> : formatDate(i?.end_at)}</td>
                      <td>{formatDate(pick(i, "created_at", "createdAt"))}</td>
                      <td><i className="bi bi-chevron-right la-go"></i></td>
                    </tr>
                  );
                })
              ) : (
                <EmptyRow cols={10} />
              )}
            </tbody>
          </table>
        </div>
      </div>
      {total > 0 && (
        <div className="mt-4">
          <CustomPagination
            total={total}
            currentPage={page}
            perPage={limit}
            onPageChange={(currentPage, perPage) => {
              setPage(currentPage);
              setLimit(perPage);
            }}
          />
        </div>
      )}
    </div>
  );
};

export default BuyerBids;
